import { Path, Vector2, Vector3, Vector4 } from 'three'
import { NURBSCurve } from 'three/examples/jsm/curves/NURBSCurve.js'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type {
  ArcEntity,
  CircleEntity,
  EllipseEntity,
  IEntity,
  PolylineEntity,
  RayEntity,
  SplineEntity,
} from '@/plugins/cad/module/CADModule/model/entity'
import { ArcUtils } from '@/plugins/cad/module/utils/ArcUtils'
import { sampleCurveByFlatness } from '@/plugins/cad/module/utils/adaptiveCurveSampling'
import { DrawUtils, type IPoint } from '@/plugins/cad/module/utils/DrawUtils'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('cad')
const sampleCache = new WeakMap<IEntity, { key: string; points: IPoint[] }>()

function cachedPoints(entity: IEntity, key: string, create: () => IPoint[]): IPoint[] {
  let cached = sampleCache.get(entity)
  if (cached?.key !== key) {
    cached = { key, points: create() }
    sampleCache.set(entity, cached)
  }
  // 调用方可修改返回点，缓存本身不向外暴露。
  return cached.points.map((point) => ({ ...point }))
}

interface IBulgeArcData {
  center: Vector2
  radius: number
  startAngle: number
  endAngle: number
  clockwise: boolean
}

/** 实体对象的通用采样工具 */
export class EntityObjectUtils {
  /** 射线渲染长度常量，单位与实体坐标一致 */
  static readonly RAY_RENDER_LENGTH = 1000

  /** 几何失效签名，包含原地可变参数，忽略选择和材质状态。 */
  static getEntityGeometryKey(entity: IEntity): string {
    switch (entity.type) {
      case EntityType.Polyline: {
        const value = entity as PolylineEntity
        return JSON.stringify([value.isClosed, value.vertices])
      }
      case EntityType.Arc: {
        const value = entity as ArcEntity
        return JSON.stringify([
          value.center,
          value.radius,
          value.startAngle,
          value.endAngle,
          value.clockwise,
        ])
      }
      case EntityType.Circle: {
        const value = entity as CircleEntity
        return JSON.stringify([value.center, value.radius])
      }
      case EntityType.Ellipse: {
        const value = entity as EllipseEntity
        return JSON.stringify([
          value.center,
          value.majorAxisEndPoint,
          value.axisRatio,
          value.startAngle,
          value.endAngle,
          value.clockwise,
        ])
      }
      case EntityType.Spline: {
        const value = entity as SplineEntity
        return JSON.stringify([value.degree, value.knots, value.controlPoints, value.weights])
      }
      default:
        return ''
    }
  }

  /**
   * 创建实体对应的线段几何体。
   *
   * @param entity - 目标实体
   * @returns 可直接用于 Line2 渲染的线段几何体；不支持的实体类型返回 null
   */
  static createEntityLineGeometry(entity: IEntity) {
    const points = EntityObjectUtils.getEntitySamplePoints(entity)
    if (!points) {
      return null
    }
    return DrawUtils.getLineGeometryByPoints(points)
  }

  /**
   * 获取实体绘制的采样点数据。
   *
   * @param entity - 目标实体
   * @returns 采样点数组；不支持的实体类型返回 null
   */
  static getEntitySamplePoints(entity: IEntity) {
    switch (entity.type) {
      case EntityType.Polyline:
        return EntityObjectUtils.getPolylineSamplePoints(entity as PolylineEntity)
      case EntityType.Arc:
      case EntityType.Circle:
        return EntityObjectUtils.getArcSamplePoints(entity as ArcEntity | CircleEntity)
      case EntityType.Spline:
        return EntityObjectUtils.getSplineSamplePoints(entity as SplineEntity)
      case EntityType.Ellipse:
        return EntityObjectUtils.getEllipseSamplePoints(entity as EllipseEntity)
      case EntityType.Ray:
        return EntityObjectUtils.getRaySamplePoints(entity as RayEntity)
      default:
        log.warn(`Unsupported entity type: ${entity.type}`)
        return null
    }
  }

  /**
   * 获取多段线采样点，考虑凸度圆弧段细分。
   *
   * @param entity - 多段线实体
   * @returns 采样后的二维点序列，闭合多段线会追加首点作为末点
   */
  static getPolylineSamplePoints(entity: PolylineEntity) {
    const key = JSON.stringify([entity.type, entity.isClosed, entity.vertices])
    return cachedPoints(entity, key, () => EntityObjectUtils.samplePolyline(entity))
  }

  private static samplePolyline(entity: PolylineEntity) {
    const points = entity.vertices.map((v) => ({ x: v.x, y: v.y, bulge: v.bulge || 0 }))
    if (entity.isClosed && points.length > 2) {
      points.push(points[0])
    }

    const sampledPoints: Vector2[] = []
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = new Vector2(points[i].x, points[i].y)
      const p2 = new Vector2(points[i + 1].x, points[i + 1].y)
      const bulge = points[i].bulge || 0

      if (Math.abs(bulge) < 0.0001) {
        sampledPoints.push(p1, p2)
      } else {
        const arcData = EntityObjectUtils.getArcDataFromBulge(p1, p2, bulge)
        if (!arcData) {
          sampledPoints.push(p1, p2)
          continue
        }
        const path = new Path()
        path.moveTo(p1.x, p1.y)
        EntityObjectUtils.addBulgeArc(path, arcData)
        const sweepAngle = ArcUtils.getSweepAngle(
          arcData.startAngle,
          arcData.endAngle,
          arcData.clockwise
        )
        const segmentsNum = ArcUtils.getStaticSegments(arcData.radius, sweepAngle)
        sampledPoints.push(...path.getPoints(segmentsNum))
      }
    }
    return sampledPoints.filter(
      (point, index) => index === 0 || !point.equals(sampledPoints[index - 1]!)
    )
  }

  /** 圆与圆弧共用段数规则，不依赖可变的 segmentsNum 旁路字段。 */
  static getArcSegments(entity: ArcEntity | CircleEntity): number {
    const arc = entity as ArcEntity
    const sweep =
      entity.type === EntityType.Circle
        ? Math.PI * 2
        : ArcUtils.getSweepAngle(arc.startAngle, arc.endAngle, arc.clockwise)
    return ArcUtils.getStaticSegments(entity.radius, sweep)
  }

  /** 普通对象使用局部点列，批次使用平移后的点列；两者共享同一缓存。 */
  static getArcSamplePoints(entity: ArcEntity | CircleEntity, local = false): IPoint[] {
    const arc = entity as ArcEntity
    const startAngle = entity.type === EntityType.Circle ? 0 : arc.startAngle
    const endAngle = entity.type === EntityType.Circle ? Math.PI * 2 : arc.endAngle
    const clockwise = entity.type === EntityType.Circle ? false : arc.clockwise
    const segmentsNum = EntityObjectUtils.getArcSegments(entity)
    const key = JSON.stringify([
      entity.type,
      entity.radius,
      startAngle,
      endAngle,
      clockwise,
      segmentsNum,
    ])
    const points = cachedPoints(entity, key, () =>
      DrawUtils.getArcPoints({
        center: { x: 0, y: 0 },
        radius: entity.radius,
        startAngle,
        endAngle,
        clockwise,
        segmentsNum,
      })
    )
    return local
      ? points
      : points.map((point) => ({ x: point.x + entity.center.x, y: point.y + entity.center.y }))
  }

  private static addBulgeArc(path: Path, arcData: IBulgeArcData) {
    const { center, radius, startAngle, endAngle, clockwise } = arcData
    path.absarc(center.x, center.y, radius, startAngle, endAngle, clockwise)
  }

  /** 根据多段线两个顶点及凸度计算圆弧参数 */
  private static getArcDataFromBulge(p1: Vector2, p2: Vector2, bulge: number) {
    if (Math.abs(bulge) < 0.0001) {
      return null
    }

    const v = new Vector2().subVectors(p2, p1)
    const L = v.length()
    const middle = new Vector2().addVectors(p1, p2).multiplyScalar(0.5)
    const normal = new Vector2(-v.y, v.x).normalize()
    const d = ((L / 2) * (1 - bulge * bulge)) / (2 * bulge)
    const center = middle.clone().add(normal.multiplyScalar(d))
    const radius = Math.sqrt((L / 2) ** 2 + d ** 2)

    const startAngle = Math.atan2(p1.y - center.y, p1.x - center.x)
    let endAngle = Math.atan2(p2.y - center.y, p2.x - center.x)

    if (bulge > 0 && endAngle <= startAngle) {
      endAngle += Math.PI * 2
    } else if (bulge < 0 && endAngle >= startAngle) {
      endAngle -= Math.PI * 2
    }

    return { center, radius, startAngle, endAngle, clockwise: bulge < 0 }
  }

  /**
   * 获取样条曲线采样点。
   *
   * @param entity - 样条实体
   * @returns 按平坦度细分的 NURBS 点列；控制点不足时退化为控制点自身
   */
  static getSplineSamplePoints(entity: SplineEntity) {
    const key = JSON.stringify([
      entity.type,
      entity.degree,
      entity.knots,
      entity.controlPoints,
      entity.weights,
    ])
    return cachedPoints(entity, key, () => EntityObjectUtils.sampleSpline(entity))
  }

  private static sampleSpline(entity: SplineEntity) {
    if (entity.controlPoints.length < entity.degree + 1) {
      return entity.controlPoints.map((pt) => new Vector3(pt.x, pt.y, 0))
    }
    const { knots, degree } = entity
    const controlPoints = entity.controlPoints.map(
      (pt, index) => new Vector4(pt.x, pt.y, 0, entity.weights?.[index] ?? 1)
    )
    const nurbsCurve = new NURBSCurve(degree, knots, controlPoints)

    const start = knots[0]!
    const length = knots[knots.length - 1]! - start
    if (!(length > 0)) return controlPoints.map((point) => new Vector3(point.x, point.y, point.z))
    const seeds = Array.from({ length: 33 }, (_, index) => index / 32)
    for (const knot of knots) {
      const parameter = (knot - start) / length
      if (parameter > 0 && parameter < 1) seeds.push(parameter)
    }
    return sampleCurveByFlatness((parameter) => nurbsCurve.getPoint(parameter), seeds)
  }

  /**
   * 获取椭圆采样点。
   *
   * @param entity - 椭圆实体
   * @returns 按起止角度与旋转角度生成的椭圆/椭圆弧采样点
   */
  static getEllipseSamplePoints(entity: EllipseEntity) {
    const { radiusX, radiusY, startAngle, endAngle, clockwise, rotation } = entity
    const segmentsNum = ArcUtils.getStaticSegments(
      Math.max(Math.abs(radiusX), Math.abs(radiusY)),
      ArcUtils.getSweepAngle(startAngle, endAngle, clockwise)
    )
    const key = JSON.stringify([
      entity.type,
      radiusX,
      radiusY,
      startAngle,
      endAngle,
      clockwise,
      rotation,
      segmentsNum,
    ])
    return cachedPoints(entity, key, () =>
      DrawUtils.getEllipsePoints({
        center: { x: 0, y: 0 },
        radiusX,
        radiusY,
        startAngle,
        endAngle,
        clockwise,
        rotation,
        segmentsNum,
      })
    )
  }

  /**
   * 获取射线采样点。
   *
   * @param entity - 射线实体
   * @returns 由起点及沿单位向量延伸 RAY_RENDER_LENGTH 的终点组成的两个点
   */
  static getRaySamplePoints(entity: RayEntity): Vector3[] {
    const start = new Vector3(entity.startPoint.x, entity.startPoint.y, 0)
    const unit = new Vector3(entity.unitVector.x, entity.unitVector.y, 0)
    const end = start.clone().add(unit.multiplyScalar(EntityObjectUtils.RAY_RENDER_LENGTH))
    return [start, end]
  }
}
